import React, { useReducer, useContext } from "react";
import reducer from "./reducer";
import {
    DISPLAY_ALERT,
    CLEAR_ALERT,
    LOGOUT_USER_BEGIN,
    LOGOUT_USER_SUCCESS,
    CREATE_COMPANY_BEGIN,
    CREATE_COMPANY_SUCCESS,
    CREATE_COMPANY_ERROR,
    SWITCH_COMPANY_BEGIN,
    SWITCH_COMPANY_SUCCESS,
    SWITCH_COMPANY_ERROR,
    SWITCH_COMPANY_TO_PROFILE_BEGIN,
    SWITCH_COMPANY_TO_PROFILE_SUCCESS,
    SWITCH_COMPANY_TO_PROFILE_ERROR,
    SWITCH_UNCLAIMED_PROFILE_BEGIN,
    SWITCH_UNCLAIMED_PROFILE_SUCCESS,
    SWITCH_UNCLAIMED_PROFILE_ERROR,
} from "./actions";
import { useMutation } from "react-query";
import IdApi from "../service/IdApi";
import LoginApi from "../service/LoginApi";
import { usStates, countries } from "../utils/AddressHelper";
import TokenService from "../utils/TokenService";
import { Mixpanel } from "../utils/Mixpanel";
import ClientHubTokenService from "../utils/ClientHubTokenService";

const userName = TokenService.getUserName();
export const initialState = {
    showAlert: false,
    alertSummary: "",
    alertDetail: "",
    alertType: "",
    isProcessing: false,
    userName: userName,
    companyId: null,
    loginEnabled: process.env.REACT_APP_LOGIN_ENABLED === "true",
    betaInterestEnabled: process.env.REACT_APP_BETA_INTEREST_ENABLED === "true",
    schedulerEnabled: process.env.REACT_APP_SCHEDULER_ENABLED === "true",
    dashboardEnabled: process.env.REACT_APP_DASHBOARD_ENABLED === "true",
    jobsEnabled: process.env.REACT_APP_JOBS_ENABLED === "true",
    addUsersEnabled: process.env.REACT_APP_ADD_USERS_ENABLED === "true",
    groupsEnabled: process.env.REACT_APP_GROUPS_ENABLED === "true",
    rolesEnabled: process.env.REACT_APP_ROLES_ENABLED === "true",
    paidPlanEnabled: process.env.REACT_APP_PAID_PLAN_ENABLED === "true",
    countries: countries,
    usStates: usStates,
};

const AppContext = React.createContext();

const AppProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const displayAlert = (severity, summary, detail) => {
        dispatch({ type: DISPLAY_ALERT, severity, summary, detail });
    };

    const clearAlert = () => {
        dispatch({
            type: CLEAR_ALERT,
        });
    };

    const logoutUser = async () => {
        if (TokenService.isAuthenticated()) {
            dispatch({ type: LOGOUT_USER_BEGIN });
            Mixpanel.reset();
            await LoginApi.get("auth/logout", {
                withCredentials: true,
            }).then((response) => {
                TokenService.logoutUser();
                dispatch({ type: LOGOUT_USER_SUCCESS });
                Mixpanel.track("UI Logout - successful");
                // try {
                //     window.FB.logout(function (response) {
                //         // they logged out
                //     })
                // }
                // catch (error) {
                //     // we'll try to log them out if they are FB
                // }
                return response.data;
            }).catch((error) => {
                TokenService.logoutUser();
                dispatch({ type: LOGOUT_USER_SUCCESS });
                // Mixpanel.track("UI Logout - API error");
            });
        }

        if (ClientHubTokenService.isAuthenticated()) {
            ClientHubTokenService.logoutClientHub();
        }
    };

    const createCompanyApi = useMutation((values) => {
        try {
            const data = IdApi.post("auth/provider", values, {
                withCredentials: true,
            }).then((response) => response.data);
            return data;
        } catch (error) {
            throw error;
        }
    });

    const createCompany = (values) => {
        dispatch({ type: CREATE_COMPANY_BEGIN });
        const { name } = values;
        createCompanyApi.mutate(values, {
            onError: (error, variables, context) => {
                // TODO there might be limits and other types of errors. We should let the user know what the error is
                displayAlert("error", "Unexpected Error", "Please try again later");
                // Mixpanel.track("Create Provider - failed API error");
                dispatch({ type: CREATE_COMPANY_ERROR });
            },
            onSuccess: (data) => {
                TokenService.setAccessToken(data.userAccessToken);
                TokenService.setRefreshToken(data.userRefreshToken);
                displayAlert("success", "Success", "Company created successfully");
                // Mixpanel.track("Create Provider - successful");
                dispatch({ type: CREATE_COMPANY_SUCCESS, companyName: name });
                if (TokenService.getProviderSubdomain()) {
                    window.location.href = TokenService.getProviderUrl();
                }
            },
        });
    };

    const switchCompanyApi = useMutation((companyId) => {
        try {
            const data = IdApi.post("auth/switch-provider", '{ "providerId": "' + companyId + '"}', {
                withCredentials: true,
            }).then((response) => response.data);
            return data;
        } catch (error) {
            throw error;
        }
    });

    const switchUnclaimedCompanyApi = useMutation((companyId) => {
        try {
            const data = IdApi.post("admin/switch-unclaimed-provider", '{ "providerId": "' + companyId + '"}', {
                withCredentials: true,
            }).then((response) => response.data);
            return data;
        } catch (error) {
            throw error;
        }
    });

    const switchCompany = (companyName, companyId) => {
        dispatch({ type: SWITCH_COMPANY_BEGIN });
        switchCompanyApi.mutate(companyId, {
            onError: (error, variables, context) => {
                displayAlert("error", "Unexpected Error", "Please try again later");
                // Mixpanel.track("Switch Provider - API error");
                dispatch({ type: SWITCH_COMPANY_ERROR });
            },
            onSuccess: (data) => {
                TokenService.setAccessToken(data.userAccessToken);
                TokenService.setRefreshToken(data.userRefreshToken);
                dispatch({ type: SWITCH_COMPANY_SUCCESS, companyName: companyName });
                // Mixpanel.track("Switch Provider - successful");
                if (TokenService.getProviderSubdomain()) {
                    window.location.href = TokenService.getProviderUrl();
                }
            },
        });
    };


    const switchCompanyToProfile = (companyName, companyId, vanityName) => {
        dispatch({ type: SWITCH_COMPANY_TO_PROFILE_BEGIN });
        switchCompanyApi.mutate(companyId, {
            onError: (error, variables, context) => {
                displayAlert("error", "Unexpected Error", "Please try again later");
                dispatch({ type: SWITCH_COMPANY_TO_PROFILE_ERROR });
            },
            onSuccess: (data) => {
                TokenService.setAccessToken(data.userAccessToken);
                TokenService.setRefreshToken(data.userRefreshToken);
                dispatch({ type: SWITCH_COMPANY_TO_PROFILE_SUCCESS, companyName: companyName });
                window.location.href = process.env.REACT_APP_NETWORK_SITE + "/company/" + vanityName;
            },
        });
    };

    const switchUnclaimedProfile = (companyName, companyId, vanityName) => {
        dispatch({ type: SWITCH_UNCLAIMED_PROFILE_BEGIN });
        switchUnclaimedCompanyApi.mutate(companyId, {
            onError: (error, variables, context) => {
                displayAlert("error", "Unexpected Error", "Please try again later");
                dispatch({ type: SWITCH_UNCLAIMED_PROFILE_ERROR });
            },
            onSuccess: (data) => {
                TokenService.setAccessToken(data.userAccessToken);
                TokenService.setRefreshToken(data.userRefreshToken);
                dispatch({ type: SWITCH_UNCLAIMED_PROFILE_SUCCESS, companyName: companyName });
                if (vanityName) {
                    // switch to profile
                    window.location.href = process.env.REACT_APP_NETWORK_SITE + "/company/" + vanityName;
                }
                else {
                    // switch to provider
                    window.location.href = TokenService.getProviderUrl();
                }
            },
        });
    };

    const setUserName = (userName) => {
        state.userName = userName;
    }

    return (
        <AppContext.Provider
            value={{
                ...state,
                displayAlert,
                clearAlert,
                logoutUser,
                createCompany,
                switchCompany,
                switchCompanyToProfile,
                setUserName,
                switchUnclaimedProfile,
            }}
        >
            {children}
        </AppContext.Provider>
    );
};
export const useAppContext = () => {
    return useContext(AppContext);
};

export { AppProvider };
